import { useMemo, useState, type ReactNode } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import {
  AlertCircle,
  Loader2,
  Mail,
  Pencil,
  RefreshCw,
  Search,
  Send,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  deleteSubscriptionNote,
  fetchMembersSubscriptions,
  generateMembersEmail,
  saveSubscriptionNote,
  sendMembersEmail,
  triggerSubscriptionSync,
} from "./membersApi";
import type { MemberSubscription } from "./types";

const MEMBERS_QUERY_KEY = ["members", "subscriptions"] as const;
const MAX_NOTE_LENGTH = 150;
const MEMBERS_STALE_TIME_MS = 5 * 60 * 1000;
const MEMBERS_GC_TIME_MS = 30 * 60 * 1000;

const membersQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: MEMBERS_STALE_TIME_MS,
      gcTime: MEMBERS_GC_TIME_MS,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

const statusClasses: Record<MemberSubscription["status"], string> = {
  ACTIVE: "bg-green-500/15 text-green-300 border border-green-500/30",
  PENDING: "bg-yellow-500/15 text-yellow-300 border border-yellow-500/30",
  EXPIRED: "bg-red-500/15 text-red-300 border border-red-500/30",
  CANCELLED: "bg-gray-500/20 text-gray-300 border border-gray-500/30",
};

const paymentClasses: Record<"OVERDUE" | "CURRENT" | "UNKNOWN", string> = {
  CURRENT: "bg-green-500/15 text-green-300 border border-green-500/30",
  OVERDUE: "bg-red-500/15 text-red-300 border border-red-500/30",
  UNKNOWN: "bg-gray-500/20 text-gray-300 border border-gray-500/30",
};

function useMembersData() {
  return useQuery({
    queryKey: MEMBERS_QUERY_KEY,
    queryFn: fetchMembersSubscriptions,
    staleTime: MEMBERS_STALE_TIME_MS,
    gcTime: MEMBERS_GC_TIME_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

function formatIsoDate(iso?: string): string {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "MMM d, yyyy");
  } catch {
    return "—";
  }
}

function formatSyncTime(iso?: string | null): string {
  if (!iso) return "Never";
  try {
    const date = parseISO(iso);
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.round(diffMs / 60_000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return format(date, "MMM d, h:mm a");
  } catch {
    return "Unknown";
  }
}

function MembersHeader({
  lastSyncedAt,
  syncStatus,
  onSync,
  isSyncing,
}: {
  lastSyncedAt?: string | null;
  syncStatus?: string;
  onSync: () => void;
  isSyncing: boolean;
}) {
  const tabs = [
    { to: "", label: "Dashboard" },
    { to: "reports", label: "Reports" },
    { to: "contact", label: "Contact" },
  ] as const;

  const syncLabel = formatSyncTime(lastSyncedAt);
  const isStale = !lastSyncedAt || Date.now() - new Date(lastSyncedAt).getTime() > 90 * 60_000;

  return (
    <header className="flex items-center justify-between border-b border-tribal-border bg-tribal-surface px-4 py-3">
      <div className="flex min-w-0 items-center gap-4">
        <h1 className="text-lg font-semibold tracking-tight">
          <span className="text-tribal-red">Tribal Axe</span> Membership
        </h1>
        <nav
          aria-label="Membership views"
          className="hidden items-center gap-0.5 rounded-lg bg-black/30 p-0.5 sm:flex"
        >
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.to === ""}
              className={({ isActive }) =>
                `rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                  isActive ? "bg-tribal-border text-white" : "text-tribal-muted hover:text-white"
                }`
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-xs ${isStale ? "text-yellow-400" : "text-tribal-muted"}`}>
          Synced {syncLabel}
          {syncStatus === "error" && <span className="ml-1 text-red-400">(last sync failed)</span>}
        </span>
        <button
          type="button"
          onClick={onSync}
          disabled={isSyncing}
          title="Refresh from Square"
          className="rounded-md p-1.5 text-tribal-muted hover:bg-white/10 hover:text-white disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
        </button>
      </div>
    </header>
  );
}

function DashboardView() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useMembersData();
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("membership");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState<MemberSubscription | null>(null);

  const subscriptions = data?.subscriptions ?? [];
  const uniqueTypes = useMemo(
    () => Array.from(new Set(subscriptions.map((s) => s.type))).sort((a, b) => a.localeCompare(b)),
    [subscriptions]
  );

  const filtered = useMemo(() => {
    return subscriptions.filter((sub) => {
      const matchesSearch = sub.name.toLowerCase().includes(searchQuery.trim().toLowerCase());
      const matchesType =
        typeFilter === "all" ||
        (typeFilter === "membership"
          ? sub.type.toLowerCase().includes("membership")
          : sub.type === typeFilter);
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "CURRENT" && sub.paymentStatus === "CURRENT") ||
        (statusFilter === "OVERDUE" && sub.paymentStatus === "OVERDUE") ||
        sub.status === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [searchQuery, typeFilter, statusFilter, subscriptions]);

  const filteredStats = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getUTCMonth();
    const currentYear = now.getUTCFullYear();
    const cancelledThisMonth = filtered.filter((sub) => {
      if (!sub.cancelledAt) return false;
      const d = new Date(sub.cancelledAt);
      return d.getUTCMonth() === currentMonth && d.getUTCFullYear() === currentYear;
    }).length;
    const newThisMonth = filtered.filter((sub) => {
      if (!sub.createdAt) return false;
      const d = new Date(sub.createdAt);
      return d.getUTCMonth() === currentMonth && d.getUTCFullYear() === currentYear;
    }).length;
    return { total: filtered.length, cancelledThisMonth, newThisMonth };
  }, [filtered]);

  const saveMutation = useMutation({
    mutationFn: async ({ id, note }: { id: string; note: string }) => {
      if (note.trim().length === 0) {
        await deleteSubscriptionNote(id);
      } else {
        await saveSubscriptionNote(id, note.trim());
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY });
      setEditing(null);
    },
  });

  return (
    <div className="p-4 sm:p-6">
      {isError && (
        <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Failed to load subscriptions: {(error as Error).message}
          </div>
          <button
            type="button"
            className="mt-2 rounded bg-white/10 px-2 py-1 text-xs text-white hover:bg-white/20"
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && (
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <StatCard label="Total Subscriptions" value={filteredStats.total} icon={<Users className="h-5 w-5" />} />
          <StatCard
            label="Cancelled This Month"
            value={filteredStats.cancelledThisMonth}
            icon={<TrendingDown className="h-5 w-5" />}
          />
          <StatCard
            label="New This Month"
            value={filteredStats.newThisMonth}
            icon={<TrendingUp className="h-5 w-5" />}
          />
        </div>
      )}

      <section className="rounded-lg border border-tribal-border bg-tribal-surface">
        <div className="border-b border-tribal-border px-4 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tribal-muted" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by member name..."
                className="w-full rounded-md border border-tribal-border bg-black/20 py-2 pl-9 pr-3 text-sm text-white placeholder:text-tribal-muted focus:border-tribal-red focus:outline-none"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="rounded-md border border-tribal-border bg-black/20 px-3 py-2 text-sm text-white focus:border-tribal-red focus:outline-none"
              >
                <option value="all">All Types</option>
                <option value="membership">Membership</option>
                {uniqueTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-md border border-tribal-border bg-black/20 px-3 py-2 text-sm text-white focus:border-tribal-red focus:outline-none"
              >
                <option value="all">All Statuses</option>
                <option value="CURRENT">Current</option>
                <option value="OVERDUE">Overdue</option>
                <option value="ACTIVE">Active</option>
                <option value="PENDING">Pending</option>
                <option value="EXPIRED">Expired</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-sm text-tribal-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading subscriptions...
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-16 text-center text-sm text-tribal-muted">
            No subscriptions match your current filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-tribal-muted">
                <tr className="border-b border-tribal-border">
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Months Active</th>
                  <th className="px-4 py-3 font-medium">Start Date</th>
                  <th className="px-4 py-3 font-medium">Renewal Date</th>
                  <th className="px-4 py-3 font-medium">Last Payment</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Current?</th>
                  <th className="px-4 py-3 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-tribal-border/60">
                {filtered.map((sub) => {
                  const payment = sub.paymentStatus ?? "UNKNOWN";
                  return (
                    <tr key={sub.id} className="hover:bg-white/[0.02]">
                      <td className="px-4 py-2 font-mono">{sub.type}</td>
                      <td className="px-4 py-2 font-medium">{sub.name}</td>
                      <td className="px-4 py-2">{sub.monthsActive ?? 0}</td>
                      <td className="px-4 py-2 text-tribal-muted">{formatIsoDate(sub.startDate)}</td>
                      <td className="px-4 py-2 text-tribal-muted">{formatIsoDate(sub.renewalDate)}</td>
                      <td className="px-4 py-2 text-tribal-muted">{formatIsoDate(sub.lastPaymentDate)}</td>
                      <td className="px-4 py-2">
                        <Badge className={statusClasses[sub.status]}>{sub.status}</Badge>
                      </td>
                      <td className="px-4 py-2">
                        <Badge className={paymentClasses[payment]}>{payment}</Badge>
                      </td>
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          onClick={() => setEditing(sub)}
                          className="flex max-w-[320px] items-center gap-2 rounded px-1 py-1 text-left text-tribal-muted hover:bg-white/5 hover:text-white"
                        >
                          <Pencil className="h-3 w-3 shrink-0" />
                          <span className="truncate">{sub.notes || "Add note..."}</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing && (
        <EditNoteDialog
          subscription={editing}
          isSaving={saveMutation.isPending}
          saveError={saveMutation.error instanceof Error ? saveMutation.error.message : null}
          onClose={() => setEditing(null)}
          onSave={(note) => saveMutation.mutate({ id: editing.id, note })}
        />
      )}
    </div>
  );
}

function ReportsView() {
  const { data, isLoading, isError, error, refetch } = useMembersData();
  const subscriptions = data?.subscriptions ?? [];

  const monthly = useMemo(() => {
    const newByMonth = new Map<string, number>();
    const cancelledByMonth = new Map<string, number>();
    for (const sub of subscriptions) {
      if (sub.createdAt) {
        const key = sub.createdAt.slice(0, 7);
        newByMonth.set(key, (newByMonth.get(key) ?? 0) + 1);
      }
      if (sub.cancelledAt) {
        const key = sub.cancelledAt.slice(0, 7);
        cancelledByMonth.set(key, (cancelledByMonth.get(key) ?? 0) + 1);
      }
    }

    const months = Array.from(new Set([...newByMonth.keys(), ...cancelledByMonth.keys()])).sort();
    return months.map((month) => ({
      month,
      label: format(parseISO(`${month}-01`), "MMM yyyy"),
      newCount: newByMonth.get(month) ?? 0,
      cancelledCount: cancelledByMonth.get(month) ?? 0,
    }));
  }, [subscriptions]);

  const summary = useMemo(() => {
    if (monthly.length === 0) return { avgNew: 0, avgCancelled: 0, netGrowth: 0 };
    const totalNew = monthly.reduce((sum, row) => sum + row.newCount, 0);
    const totalCancelled = monthly.reduce((sum, row) => sum + row.cancelledCount, 0);
    return {
      avgNew: Math.round((totalNew / monthly.length) * 10) / 10,
      avgCancelled: Math.round((totalCancelled / monthly.length) * 10) / 10,
      netGrowth: Math.round(((totalNew - totalCancelled) / monthly.length) * 10) / 10,
    };
  }, [monthly]);

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <StatCard label="Avg New / Month" value={summary.avgNew} icon={<TrendingUp className="h-5 w-5" />} />
        <StatCard
          label="Avg Cancelled / Month"
          value={summary.avgCancelled}
          icon={<TrendingDown className="h-5 w-5" />}
        />
        <StatCard
          label="Net Growth / Month"
          value={`${summary.netGrowth > 0 ? "+" : ""}${summary.netGrowth}`}
          icon={<Users className="h-5 w-5" />}
        />
      </div>

      {isError && (
        <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Failed to load reports data: {(error as Error).message}
          </div>
          <button
            type="button"
            className="mt-2 rounded bg-white/10 px-2 py-1 text-xs text-white hover:bg-white/20"
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      )}

      <section className="overflow-x-auto rounded-lg border border-tribal-border bg-tribal-surface">
        <table className="min-w-full text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-tribal-muted">
            <tr className="border-b border-tribal-border">
              <th className="px-4 py-3 font-medium">Month</th>
              <th className="px-4 py-3 font-medium">New</th>
              <th className="px-4 py-3 font-medium">Cancelled</th>
              <th className="px-4 py-3 font-medium">Net</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-tribal-border/60">
            {isLoading ? (
              <tr>
                <td className="px-4 py-10 text-center text-tribal-muted" colSpan={4}>
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading report rows...
                  </span>
                </td>
              </tr>
            ) : monthly.length === 0 ? (
              <tr>
                <td className="px-4 py-10 text-center text-tribal-muted" colSpan={4}>
                  No monthly data available yet.
                </td>
              </tr>
            ) : (
              monthly.map((row) => (
                <tr key={row.month} className="hover:bg-white/[0.02]">
                  <td className="px-4 py-2">{row.label}</td>
                  <td className="px-4 py-2 text-green-300">{row.newCount}</td>
                  <td className="px-4 py-2 text-red-300">{row.cancelledCount}</td>
                  <td className="px-4 py-2">{row.newCount - row.cancelledCount}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function ContactView() {
  const { data, isLoading, isError, error, refetch } = useMembersData();
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [lastResult, setLastResult] = useState<{ kind: "success" | "error"; message: string } | null>(null);

  const subscriptions = data?.subscriptions ?? [];
  const filtered = useMemo(
    () =>
      subscriptions.filter((s) => {
        if (!s.email) return false;
        if (statusFilter === "all") return true;
        return s.status === statusFilter;
      }),
    [subscriptions, statusFilter]
  );

  const selectedEmails = useMemo(
    () =>
      filtered
        .filter((sub) => selectedIds.has(sub.id) && !!sub.email)
        .map((sub) => sub.email as string),
    [filtered, selectedIds]
  );

  const selectedMembers = useMemo(
    () =>
      filtered
        .filter((sub) => selectedIds.has(sub.id))
        .map((sub) => ({
          name: sub.name,
          status: sub.status,
          renewalDate: sub.renewalDate,
          paymentStatus: sub.paymentStatus,
        })),
    [filtered, selectedIds]
  );

  const generateMutation = useMutation({
    mutationFn: async () => {
      return generateMembersEmail(aiPrompt.trim(), selectedMembers);
    },
    onSuccess: (res) => {
      setSubject(res.subject);
      setBody(res.body);
      setLastResult({ kind: "success", message: "Email generated — review and edit below." });
    },
    onError: (err) => {
      setLastResult({ kind: "error", message: `AI generation failed: ${(err as Error).message}` });
    },
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      return sendMembersEmail({
        to: selectedEmails,
        subject: subject.trim(),
        html: body.replace(/\n/g, "<br>"),
      });
    },
    onSuccess: (res) => {
      setLastResult({
        kind: "success",
        message: `Sent to ${selectedEmails.length} recipient${selectedEmails.length === 1 ? "" : "s"} (id: ${res.id}).`,
      });
      setSubject("");
      setBody("");
      setAiPrompt("");
      setSelectedIds(new Set());
    },
    onError: (err) => {
      setLastResult({ kind: "error", message: (err as Error).message });
    },
  });

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const allSelected = filtered.length > 0 && filtered.every((sub) => selectedIds.has(sub.id));
  const canSend = selectedEmails.length > 0 && subject.trim().length > 0 && body.trim().length > 0;

  return (
    <div className="p-4 sm:p-6">
      {isError && (
        <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Failed to load member emails: {(error as Error).message}
          </div>
          <button
            type="button"
            className="mt-2 rounded bg-white/10 px-2 py-1 text-xs text-white hover:bg-white/20"
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      )}

      {lastResult && (
        <div
          className={`mb-4 rounded-lg border p-3 text-sm ${
            lastResult.kind === "success"
              ? "border-green-500/40 bg-green-500/10 text-green-200"
              : "border-red-500/40 bg-red-500/10 text-red-200"
          }`}
        >
          {lastResult.message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <section className="rounded-lg border border-tribal-border bg-tribal-surface lg:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-tribal-border px-4 py-3">
            <h2 className="text-sm font-semibold">Members ({selectedEmails.length} selected)</h2>
            <div className="flex flex-wrap gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-md border border-tribal-border bg-black/20 px-3 py-1.5 text-sm text-white focus:border-tribal-red focus:outline-none"
              >
                <option value="all">All Statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="PENDING">Pending</option>
                <option value="EXPIRED">Expired</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
              <button
                type="button"
                onClick={() =>
                  allSelected ? setSelectedIds(new Set()) : setSelectedIds(new Set(filtered.map((s) => s.id)))
                }
                className="rounded-md bg-white/5 px-3 py-1.5 text-sm text-tribal-muted hover:bg-white/10 hover:text-white"
              >
                {allSelected ? "Deselect All" : "Select All"}
              </button>
            </div>
          </div>

          <div className="max-h-[520px] overflow-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-tribal-muted">
                <tr className="border-b border-tribal-border">
                  <th className="px-4 py-3 font-medium">Select</th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-tribal-border/60">
                {isLoading ? (
                  <tr>
                    <td className="px-4 py-10 text-center text-tribal-muted" colSpan={4}>
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading members...
                      </span>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td className="px-4 py-10 text-center text-tribal-muted" colSpan={4}>
                      No members with email addresses match this filter.
                    </td>
                  </tr>
                ) : (
                  filtered.map((sub) => (
                    <tr key={sub.id} className="cursor-pointer hover:bg-white/[0.02]" onClick={() => toggleSelect(sub.id)}>
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(sub.id)}
                          onChange={() => toggleSelect(sub.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-4 py-2 font-medium">{sub.name}</td>
                      <td className="px-4 py-2 text-tribal-muted">{sub.email}</td>
                      <td className="px-4 py-2">
                        <Badge className={statusClasses[sub.status]}>{sub.status}</Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Mail className="h-4 w-4 text-tribal-red" />
            Compose Email
          </h2>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-tribal-muted">To</label>
              <div className="rounded-md border border-tribal-border bg-black/20 px-3 py-2 text-sm text-tribal-muted">
                {selectedEmails.length === 0
                  ? "Select members from the list"
                  : `${selectedEmails.length} recipient${selectedEmails.length === 1 ? "" : "s"}`}
              </div>
            </div>

            <div className="rounded-md border border-purple-500/30 bg-purple-500/5 p-3">
              <label htmlFor="ai-prompt" className="mb-1.5 flex items-center gap-1.5 text-xs uppercase tracking-wide text-purple-300">
                <Sparkles className="h-3.5 w-3.5" />
                AI Email Generator
              </label>
              <textarea
                id="ai-prompt"
                rows={3}
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                className="w-full rounded-md border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white placeholder:text-tribal-muted focus:border-purple-500 focus:outline-none"
                placeholder="Describe the email you want to send... e.g. 'Renewal reminder for members expiring this month' or 'Invite to member's only night this Friday'"
              />
              <button
                type="button"
                disabled={aiPrompt.trim().length === 0 || generateMutation.isPending}
                onClick={() => generateMutation.mutate()}
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {generateMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" /> Generate with AI
                  </>
                )}
              </button>
            </div>

            <div>
              <label htmlFor="members-subject" className="mb-1 block text-xs uppercase tracking-wide text-tribal-muted">
                Subject
              </label>
              <input
                id="members-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded-md border border-tribal-border bg-black/20 px-3 py-2 text-sm text-white placeholder:text-tribal-muted focus:border-tribal-red focus:outline-none"
                placeholder="Renewal reminder"
              />
            </div>
            <div>
              <label htmlFor="members-body" className="mb-1 block text-xs uppercase tracking-wide text-tribal-muted">
                Message
              </label>
              <textarea
                id="members-body"
                rows={10}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="w-full rounded-md border border-tribal-border bg-black/20 px-3 py-2 text-sm text-white placeholder:text-tribal-muted focus:border-tribal-red focus:outline-none"
                placeholder="Hi {{name}}, ..."
              />
            </div>
            <button
              type="button"
              disabled={!canSend || sendMutation.isPending}
              onClick={() => sendMutation.mutate()}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-tribal-red px-3 py-2 text-sm font-medium text-white hover:bg-tribal-red-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sendMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" /> Send Email
                </>
              )}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function EditNoteDialog(props: {
  subscription: MemberSubscription;
  isSaving: boolean;
  saveError: string | null;
  onSave: (note: string) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState(props.subscription.notes ?? "");
  const remaining = MAX_NOTE_LENGTH - note.length;
  const changed = note !== (props.subscription.notes ?? "");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-lg border border-tribal-border bg-tribal-surface p-4 shadow-2xl">
        <h3 className="text-base font-semibold">Edit note</h3>
        <p className="mt-1 text-sm text-tribal-muted">
          {props.subscription.name} — {props.subscription.type}
        </p>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={5}
          maxLength={MAX_NOTE_LENGTH}
          className="mt-3 w-full rounded-md border border-tribal-border bg-black/20 px-3 py-2 text-sm text-white placeholder:text-tribal-muted focus:border-tribal-red focus:outline-none"
          placeholder="Add a note about this subscription..."
        />
        <div className="mt-1 text-xs text-tribal-muted">{remaining} characters remaining</div>
        {props.saveError && (
          <div className="mt-2 rounded border border-red-500/40 bg-red-500/10 px-2 py-1 text-xs text-red-200">
            {props.saveError}
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2">
          {(props.subscription.notes ?? "").length > 0 && (
            <button
              type="button"
              disabled={props.isSaving}
              onClick={() => props.onSave("")}
              className="rounded-md bg-white/5 px-3 py-1.5 text-sm text-tribal-muted hover:bg-white/10 hover:text-white disabled:opacity-60"
            >
              Delete
            </button>
          )}
          <button
            type="button"
            disabled={props.isSaving}
            onClick={props.onClose}
            className="rounded-md bg-white/5 px-3 py-1.5 text-sm text-tribal-muted hover:bg-white/10 hover:text-white disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={props.isSaving || !changed}
            onClick={() => props.onSave(note)}
            className="inline-flex items-center gap-2 rounded-md bg-tribal-red px-3 py-1.5 text-sm font-medium text-white hover:bg-tribal-red-dark disabled:opacity-60"
          >
            {props.isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Badge(props: { className: string; children: ReactNode }) {
  return <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${props.className}`}>{props.children}</span>;
}

function StatCard(props: { label: string; value: number | string; icon: ReactNode }) {
  return (
    <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-tribal-muted">{props.label}</p>
          <p className="mt-1 text-3xl font-semibold text-white">{props.value}</p>
        </div>
        <div className="rounded-lg bg-tribal-red/15 p-2 text-tribal-red">{props.icon}</div>
      </div>
    </div>
  );
}

function MembersModuleInner() {
  const queryClient = useQueryClient();
  const { data } = useMembersData();

  const syncMutation = useMutation({
    mutationFn: triggerSubscriptionSync,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY });
    },
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      <MembersHeader
        lastSyncedAt={data?.lastSyncedAt}
        syncStatus={data?.syncStatus}
        onSync={() => syncMutation.mutate()}
        isSyncing={syncMutation.isPending}
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Routes>
          <Route index element={<DashboardView />} />
          <Route path="reports" element={<ReportsView />} />
          <Route path="contact" element={<ContactView />} />
          <Route path="*" element={<Navigate to="." replace />} />
        </Routes>
      </div>
    </div>
  );
}

export function MembersModule() {
  return (
    <QueryClientProvider client={membersQueryClient}>
      <MembersModuleInner />
    </QueryClientProvider>
  );
}
